import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ContactForm from "./ContactUs";
import HowItWorks from "../customized/HowItWorks";
import { trackPageView, getSessionId, getUserId } from "../utils/pageTracking";

const DropOffSignUpForm: React.FC = () => {
    useEffect(() => {
        // Track page view
        trackPageView('DropOffSignUp', getSessionId(), getUserId());
    }, []);

    const { state } = useLocation();
    const {
        title,
        mapUrl,
        website,
        locationNote,
    } = state || {};

    return (
        <div className="flex flex-col items-center bg-[#fafaf3] min-h-screen pt-16">
            {/* Navigation Link */}
            <div className="w-full bg-white/90 py-3 px-6">
                <div className="container mx-auto">
                    <Link
                        to="/drop-off-locations"
                        className="inline-flex items-center text-[#b05e2c] hover:text-orange-700 transition-colors font-semibold"
                    >
                        ← Back to All Drop Off Locations
                    </Link>
                </div>
            </div>

            {/* Hero Section */}
            <section className="w-full bg-[#c3d3ab] text-white py-8">
                <div className="container mx-auto px-6 text-center">
                    <h1 className="font-mono text-orange-600 text-4xl md:text-5xl lg:text-6xl mb-4">
                        Bangor Drop Off Site:  <a
                            href={website}
                            target="_blank"
                            rel="noreferrer"
                            className="underline hover:text-orange-700 transition-colors"
                        >
                            {title}
                        </a>
                    </h1>
                    <p className="text-xl md:text-2xl max-w-2xl mx-auto text-black">
                        {locationNote}
                    </p>
                </div>
            </section>

            {/* Picture Section */}
            <section className="w-full px-6 py-6">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Left Image - Google Maps */}
                        <div className="relative h-100 overflow-hidden rounded-lg">
                            <iframe
                                title="Location Map"
                                src={mapUrl}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="absolute inset-0"
                            />

                        </div>
                        {/* Right Image */}
                        <div className="relative h-100 overflow-hidden rounded-lg">
                            <img
                                src="/images/dropOff.avif" // Update with your image path
                                alt="Drop-off box"
                                className="object-cover w-full h-full"
                            />
                            <div className="absolute text-center bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4">
                                <p className="text-lg font-mono">Drop-off Box</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <HowItWorks
                steps={[
                    {
                        title: "Sign Up",
                        description: "Fill out the form below to join our drop-off program",
                    },
                    {
                        title: "Collect",
                        description: "Gather your compostable materials in a designated container",
                    },
                    {
                        title: "Drop Off",
                        description: "Bring your materials to our convenient drop-off location",
                    },
                ]}
            />

            {/* Contact Form Section */}
            <section className="container bg-[#c3d3ab] mx-auto px-6 py-6">
                <ContactForm
                    headingText="Sign Up Now"
                    subText="Ready to start composting? Fill out the form below and we'll get you started."
                    showPhone={true}
                    showSubject={false}
                    subjectText="Drop Off Sign Up Inquiry"
                    headingClassName="font-mono text-[#b05e2c]"
                    messageName="Refered By"
                    preferredInput="text"
                    submitButtonText="Sign Up $15/month"
                    showDropdown={true}
                    dropdownLabel="Drop Off Location"
                    dropdownOptions={[
                        "Bangor : RIZE",
                        "Bangor : Northwood Portable Buildings",
                        "Coming Soon"
                    ]}
                    defaultDropdownValue="Bangor : RIZE"
                />
            </section>

            {/* Footer Section */}
            <h2 className="font-mono text-3xl text-[#b05e2c] m-6 text-center">
                We would like to thank{" "}
                <a
                    href={website}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-orange-700 transition-colors"
                >
                    {title}
                </a>
                {" "}for collaborating with us by hosting a drop off location!
            </h2>

        </div>
    );
};

export default DropOffSignUpForm;
